/**
 * FCEA Tracker: Seed Data (Generación Dinámica)
 * Malla completa: Tecnicatura en Administración (FCEA, Udelar)
 *
 * Semántica de `requisitos: Requisito[][]`
 *   Outer [] = AND  → todos los grupos deben cumplirse
 *   Inner [] = OR   → basta con que UN item del grupo se cumpla
 */

import { Asignatura } from '../logic/types';

export const PLAN_ID_TECNICATURA = 'plan_tecnicatura_admin_fcea';

export const generarMallaTEA = (opcionCalculo: 'unificado' | 'desdoblado'): Asignatura[] => {
  const asignaturas: Asignatura[] = [
    // ─────────────────────────────────────────
    // SEMESTRE 1: Obligatorias Comunes
    // ─────────────────────────────────────────
    {
      id: 'A10',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Admin. y Gestión I',
      codigo: 'A10',
      semestre_sugerido: 1,
      creditos: 10,
      departamento: 'Administración',
      estado: 'no_iniciada',
      es_opcional: false,
      obligatoria: true,
      dictado: 'ambos',
      requisitos: [],
    },
    {
      id: 'C10',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Conceptos Contables',
      codigo: 'C10',
      semestre_sugerido: 1,
      creditos: 10,
      departamento: 'Contabilidad',
      estado: 'no_iniciada',
      es_opcional: false,
      obligatoria: true,
      dictado: 'ambos',
      requisitos: [],
    },
    {
      id: 'E10',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Intro a la Microeconomía',
      codigo: 'E10',
      semestre_sugerido: 1,
      creditos: 10,
      departamento: 'Economía',
      estado: 'no_iniciada',
      es_opcional: false,
      obligatoria: true,
      dictado: 'ambos',
      requisitos: [],
    },
  ];

  // Cálculo condicional
  if (opcionCalculo === 'unificado') {
    asignaturas.push({
      id: 'MC10',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Cálculo I',
      codigo: 'MC10',
      semestre_sugerido: 1,
      creditos: 10,
      departamento: 'Métodos Cuantitativos',
      estado: 'no_iniciada',
      es_opcional: false,
      obligatoria: true,
      dictado: 'ambos',
      requisitos: [],
    });
  } else {
    asignaturas.push(
      {
        id: '114A',
        plan_id: PLAN_ID_TECNICATURA,
        nombre: 'Cálculo I/A',
        codigo: '114A',
        semestre_sugerido: 1,
        creditos: 5,
        departamento: 'Métodos Cuantitativos',
        estado: 'no_iniciada',
        es_opcional: false,
        obligatoria: true,
        dictado: 'ambos',
        requisitos: [],
      },
      {
        id: '128A',
        plan_id: PLAN_ID_TECNICATURA,
        nombre: 'Cálculo I/B',
        codigo: '128A',
        semestre_sugerido: 1,
        creditos: 5,
        departamento: 'Métodos Cuantitativos',
        estado: 'no_iniciada',
        es_opcional: false,
        obligatoria: true,
        dictado: 'ambos',
        requisitos: [
          [{ tipo: 'materia_aprobada', id: '114A' }],
        ],
      }
    );
  }

  // Previas para asignaturas dependientes
  const previaCalculo = opcionCalculo === 'unificado' ? 'MC10' : '128A';

  asignaturas.push(
    // ─────────────────────────────────────────
    // SEMESTRE 2: Obligatorias
    // ─────────────────────────────────────────
    {
      id: 'A20',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Admin. y Gestión II',
      codigo: 'A20',
      semestre_sugerido: 2,
      creditos: 10,
      departamento: 'Administración',
      estado: 'no_iniciada',
      es_opcional: false,
      obligatoria: true,
      dictado: 'par',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: 'A10' }],
      ],
    },
    {
      id: 'C20',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Contabilidad General I',
      codigo: 'C20',
      semestre_sugerido: 2,
      creditos: 10,
      departamento: 'Contabilidad',
      estado: 'no_iniciada',
      es_opcional: false,
      obligatoria: true,
      dictado: 'par',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: 'C10' }],
      ],
    },
    {
      id: 'J20',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Derecho Civil',
      codigo: 'J20',
      semestre_sugerido: 2,
      creditos: 10,
      departamento: 'Jurídico',
      estado: 'no_iniciada',
      es_opcional: false,
      obligatoria: true,
      dictado: 'par',
      requisitos: [],
    },

    // ─────────────────────────────────────────
    // SEMESTRE 3
    // ─────────────────────────────────────────
    {
      id: 'A30',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Procesos y Sist. de Información',
      codigo: 'A30',
      semestre_sugerido: 3,
      creditos: 10,
      departamento: 'Administración',
      estado: 'no_iniciada',
      es_opcional: false,
      obligatoria: true,
      dictado: 'impar',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: 'A10' }],
      ],
    },
    {
      id: 'C30',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Contabilidad General II',
      codigo: 'C30',
      semestre_sugerido: 3,
      creditos: 10,
      departamento: 'Contabilidad',
      estado: 'no_iniciada',
      es_opcional: false,
      obligatoria: true,
      dictado: 'impar',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: 'C20' }],
      ],
    },
    {
      id: 'J30',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Derecho Comercial',
      codigo: 'J30',
      semestre_sugerido: 3,
      creditos: 10,
      departamento: 'Jurídico',
      estado: 'no_iniciada',
      es_opcional: false,
      obligatoria: true,
      dictado: 'impar',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: 'J20' }],
      ],
    },
    {
      id: 'MC40',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Intro a la Estadística',
      codigo: 'MC40',
      semestre_sugerido: 3,
      creditos: 10,
      departamento: 'Métodos Cuantitativos',
      estado: 'no_iniciada',
      es_opcional: false,
      obligatoria: true,
      dictado: 'impar',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: previaCalculo }],
      ],
    },
    {
      id: 'A61',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Marketing Básico',
      codigo: 'A61',
      semestre_sugerido: 3,
      creditos: 10,
      departamento: 'Administración',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'impar',
      requisitos: [],
    },

    // ─────────────────────────────────────────
    // SEMESTRE 4
    // ─────────────────────────────────────────
    {
      id: 'A40',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Admin. de Recursos Humanos',
      codigo: 'A40',
      semestre_sugerido: 4,
      creditos: 10,
      departamento: 'Administración',
      estado: 'no_iniciada',
      es_opcional: false,
      obligatoria: true,
      dictado: 'par',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: 'A20' }],
      ],
    },
    {
      id: 'A42',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Diseño Organizacional',
      codigo: 'A42',
      semestre_sugerido: 4,
      creditos: 10,
      departamento: 'Administración',
      estado: 'no_iniciada',
      es_opcional: false,
      obligatoria: true,
      dictado: 'par',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: 'A30' }],
        [{ tipo: 'materia_aprobada', id: 'A20' }],
      ],
    },
    {
      id: 'C40',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Contabilidad General III',
      codigo: 'C40',
      semestre_sugerido: 4,
      creditos: 10,
      departamento: 'Contabilidad',
      estado: 'no_iniciada',
      es_opcional: false,
      obligatoria: true,
      dictado: 'par',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: 'C30' }],
      ],
    },
    {
      id: 'MC30',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Matemática Financiera',
      codigo: 'MC30',
      semestre_sugerido: 4,
      creditos: 10,
      departamento: 'Métodos Cuantitativos',
      estado: 'no_iniciada',
      es_opcional: false,
      obligatoria: true,
      dictado: 'par',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: previaCalculo }],
        [{ tipo: 'materia_aprobada', id: 'MC40' }],
      ],
    },
    {
      id: 'C41',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Legislación Laboral',
      codigo: 'C41',
      semestre_sugerido: 4,
      creditos: 10,
      departamento: 'Contabilidad',
      estado: 'no_iniciada',
      es_opcional: false,
      obligatoria: true,
      dictado: 'par',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: 'C10' }],
        [{ tipo: 'materia_aprobada', id: 'J20' }],
      ],
    },
    {
      id: 'A44',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Compras Públicas',
      codigo: 'A44',
      semestre_sugerido: 4,
      creditos: 10,
      departamento: 'Administración',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'par',
      requisitos: [
        [{ tipo: 'creditos_minimos', valor: 120 }],
      ],
    },
    {
      id: 'I87',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Prospectiva Estratégica',
      codigo: 'I87',
      semestre_sugerido: 4,
      creditos: 10,
      departamento: 'Interdisciplinario',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'par',
      requisitos: [
        [{ tipo: 'creditos_minimos', valor: 180 }],
      ],
    },
    {
      id: 'I41',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Economía Circular',
      codigo: 'I41',
      semestre_sugerido: 4,
      creditos: 10,
      departamento: 'Interdisciplinario',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'par',
      requisitos: [
        [{ tipo: 'creditos_minimos', valor: 100 }],
      ],
    },
    {
      id: 'I97',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'EFI Contabilidad Escuelas',
      codigo: 'I97',
      semestre_sugerido: 4,
      creditos: 10,
      departamento: 'Interdisciplinario',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'par',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: 'C30' }],
      ],
    },
    {
      id: 'I114D',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'EFI Costos Emprendimientos',
      codigo: 'I114D',
      semestre_sugerido: 4,
      creditos: 10,
      departamento: 'Interdisciplinario',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'par',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: 'C31' }],
      ],
    },
    {
      id: 'J60OP',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Derecho Público',
      codigo: 'J60OP',
      semestre_sugerido: 4,
      creditos: 10,
      departamento: 'Jurídico',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'par',
      requisitos: [],
    },
    {
      id: 'A67',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Gestión Financiera del Estado',
      codigo: 'A67',
      semestre_sugerido: 4,
      creditos: 10,
      departamento: 'Administración',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'par',
      requisitos: [
        [{ tipo: 'creditos_minimos', valor: 120 }],
      ],
    },
    {
      id: 'A83',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Cambio Organizacional',
      codigo: 'A83',
      semestre_sugerido: 4,
      creditos: 10,
      departamento: 'Administración',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'par',
      requisitos: [
        [{ tipo: 'creditos_minimos', valor: 120 }],
      ],
    },
    {
      id: 'A72',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Marketing Estratégico',
      codigo: 'A72',
      semestre_sugerido: 4,
      creditos: 10,
      departamento: 'Administración',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'par',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: 'A61' }],
      ],
    },

    // ─────────────────────────────────────────
    // SEMESTRE 5
    // ─────────────────────────────────────────
    {
      id: 'A70',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Comportamiento Organizacional',
      codigo: 'A70',
      semestre_sugerido: 5,
      creditos: 10,
      departamento: 'Administración',
      estado: 'no_iniciada',
      es_opcional: false,
      obligatoria: true,
      dictado: 'impar',
      requisitos: [
        [{ tipo: 'creditos_minimos', valor: 170 }],
      ],
    },
    {
      id: 'A86',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Transformación Cultural',
      codigo: 'A86',
      semestre_sugerido: 5,
      creditos: 10,
      departamento: 'Administración',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'impar',
      requisitos: [
        [{ tipo: 'creditos_minimos', valor: 150 }],
      ],
    },
    {
      id: 'I112',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'EFI Cooperativas',
      codigo: 'I112',
      semestre_sugerido: 5,
      creditos: 10,
      departamento: 'Interdisciplinario',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'impar',
      requisitos: [
        [{ tipo: 'creditos_minimos', valor: 120 }],
      ],
    },
    {
      id: 'A85',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Taller Gestión de Cargos',
      codigo: 'A85',
      semestre_sugerido: 5,
      creditos: 5,
      departamento: 'Administración',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'impar',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: 'A40' }],
      ],
    },
    {
      id: 'I111N',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'EFI MicroCECEA',
      codigo: 'I111N',
      semestre_sugerido: 5,
      creditos: 10,
      departamento: 'Interdisciplinario',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'impar',
      requisitos: [
        [{ tipo: 'creditos_minimos', valor: 120 }],
      ],
    },
    {
      id: 'I52',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Ética y Ejercicio Profesional',
      codigo: 'I52',
      semestre_sugerido: 5,
      creditos: 5,
      departamento: 'Interdisciplinario',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'impar',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: 'C30' }],
        [{ tipo: 'materia_aprobada', id: 'J30' }],
      ],
    },
    {
      id: 'A50',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Análisis Inst. y Planificación',
      codigo: 'A50',
      semestre_sugerido: 5,
      creditos: 10,
      departamento: 'Administración',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'impar',
      requisitos: [
        [{ tipo: 'creditos_minimos', valor: 90 }],
      ],
    },
    {
      id: 'A52',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Marketing de Servicios',
      codigo: 'A52',
      semestre_sugerido: 5,
      creditos: 10,
      departamento: 'Administración',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'impar',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: 'A72' }],
      ],
    },
    {
      id: 'A77',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Marketing Digital',
      codigo: 'A77',
      semestre_sugerido: 5,
      creditos: 10,
      departamento: 'Administración',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'impar',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: 'A72' }],
      ],
    },
    {
      id: 'I56',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Liderazgo Innovación Social',
      codigo: 'I56',
      semestre_sugerido: 5,
      creditos: 10,
      departamento: 'Interdisciplinario',
      estado: 'no_iniciada',
      es_opcional: true,
      obligatoria: false,
      dictado: 'impar',
      requisitos: [
        [{ tipo: 'creditos_minimos', valor: 120 }],
      ],
    },
    {
      id: 'C31',
      plan_id: PLAN_ID_TECNICATURA,
      nombre: 'Costos para la Gestión',
      codigo: 'C31',
      semestre_sugerido: 5,
      creditos: 10,
      departamento: 'Contabilidad',
      estado: 'no_iniciada',
      es_opcional: false,
      obligatoria: true,
      dictado: 'impar',
      requisitos: [
        [{ tipo: 'materia_aprobada', id: 'C40' }],
      ],
    },
  );

  return asignaturas;
};
